import { useState, useEffect } from 'react'
import { 
  Settings as SettingsIcon, Moon, Sun, Bell, Shield, 
  Globe, Palette, Monitor, Save, Check, X, Eye, EyeOff,
  Mail, MessageSquare, Calendar, FileText, AlertTriangle, Lock
} from 'lucide-react'
import { useTheme } from '../context/ThemeContext'
import { useAuthStore } from '../store/authStore'
import api from '../lib/api'
import toast from 'react-hot-toast'
import { requestNotificationPermission } from '../services/notificationService'

const Settings = () => {
  const { isDarkMode, toggleTheme } = useTheme()
  const { user, setUser } = useAuthStore()
  
  const [activeTab, setActiveTab] = useState('appearance')
  const [saving, setSaving] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [changingPassword, setChangingPassword] = useState(false)
  
  // Settings state
  const [settings, setSettings] = useState({
    // Appearance
    theme: isDarkMode ? 'dark' : 'light',
    accentColor: 'blue',
    compactMode: false,
    
    // Notifications
    emailNotifications: true,
    pushNotifications: true,
    tenderAlerts: true,
    proposalUpdates: true,
    eventReminders: true,
    weeklyDigest: false,
    
    // Privacy
    showOnlineStatus: true,
    showActivityStatus: true,
    allowDataCollection: true,
    
    // General
    language: 'en',
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    currency: 'INR'
  })

  useEffect(() => {
    // Load user preferences if available
    if (user?.preferences) {
      setSettings(prev => ({ ...prev, ...user.preferences }))
    }
    // Sync theme state
    setSettings(prev => ({ ...prev, theme: isDarkMode ? 'dark' : 'light' }))
  }, [user, isDarkMode])

  const handleSettingChange = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
    
    // Immediately apply theme changes
    if (key === 'theme') {
      if (value === 'system') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        if ((prefersDark && !isDarkMode) || (!prefersDark && isDarkMode)) {
          toggleTheme()
        }
      } else if ((value === 'dark' && !isDarkMode) || (value === 'light' && isDarkMode)) {
        toggleTheme()
      }
      toast.success(`Theme changed to ${value}`)
    }
    
    // Handle push notifications permission
    if (key === 'pushNotifications' && value === true) {
      const granted = await requestNotificationPermission()
      if (!granted) {
        setSettings(prev => ({ ...prev, pushNotifications: false }))
        toast.error('Browser notification permission denied')
        return
      }
      toast.success('Push notifications enabled')
    }
    
    // Apply compact mode
    if (key === 'compactMode') {
      document.documentElement.classList.toggle('compact-mode', value)
      localStorage.setItem('compactMode', value)
      toast.success(value ? 'Compact mode enabled' : 'Compact mode disabled')
    }
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    try {
      const response = await api.put('/auth/preferences', { preferences: settings })
      if (setUser && response.data) {
        setUser(response.data)
      }
      // Store settings locally as backup
      localStorage.setItem('userSettings', JSON.stringify(settings))
      toast.success('Settings saved successfully')
    } catch (error) {
      console.error('Failed to save settings:', error)
      // Still save locally
      localStorage.setItem('userSettings', JSON.stringify(settings))
      toast.success('Settings saved locally')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match')
      return
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters')
      return
    }
    
    setChangingPassword(true)
    try {
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })
      toast.success('Password changed successfully')
      setShowPasswordModal(false)
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to change password')
    } finally {
      setChangingPassword(false)
    }
  }

  const tabs = [
    { id: 'appearance', name: 'Appearance', icon: Palette },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'privacy', name: 'Privacy & Security', icon: Shield },
    { id: 'general', name: 'General', icon: Globe },
  ]

  const accentColors = [
    { id: 'blue', color: 'bg-blue-500', name: 'Blue' },
    { id: 'purple', color: 'bg-purple-500', name: 'Purple' },
    { id: 'green', color: 'bg-green-500', name: 'Green' },
    { id: 'orange', color: 'bg-orange-500', name: 'Orange' },
    { id: 'pink', color: 'bg-pink-500', name: 'Pink' },
    { id: 'cyan', color: 'bg-cyan-500', name: 'Cyan' },
  ]

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <SettingsIcon className="w-7 h-7" />
          Settings
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Tabs */}
        <div className="lg:w-64 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
            
            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Theme</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'light', icon: Sun, label: 'Light' },
                      { id: 'dark', icon: Moon, label: 'Dark' },
                      { id: 'system', icon: Monitor, label: 'System' },
                    ].map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => handleSettingChange('theme', theme.id)}
                        className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                          settings.theme === theme.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <theme.icon className={`w-6 h-6 ${settings.theme === theme.id ? 'text-blue-500' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${settings.theme === theme.id ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {theme.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Accent Color</h3>
                  <div className="flex flex-wrap gap-3">
                    {accentColors.map((color) => (
                      <button
                        key={color.id}
                        onClick={() => handleSettingChange('accentColor', color.id)}
                        className={`w-10 h-10 rounded-xl ${color.color} flex items-center justify-center transition-transform hover:scale-110 ${
                          settings.accentColor === color.id ? 'ring-2 ring-offset-2 ring-gray-400 dark:ring-offset-gray-800' : ''
                        }`}
                        title={color.name}
                      >
                        {settings.accentColor === color.id && <Check className="w-5 h-5 text-white" />}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Display</h3>
                  <ToggleSetting
                    label="Compact Mode"
                    description="Reduce spacing and padding for a denser layout"
                    checked={settings.compactMode}
                    onChange={(v) => handleSettingChange('compactMode', v)}
                  />
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notification Channels</h3>
                  <div className="space-y-4">
                    <ToggleSetting
                      label="Email Notifications"
                      description="Receive notifications via email"
                      icon={Mail}
                      checked={settings.emailNotifications}
                      onChange={(v) => handleSettingChange('emailNotifications', v)}
                    />
                    <ToggleSetting
                      label="Push Notifications"
                      description="Receive browser push notifications"
                      icon={Bell}
                      checked={settings.pushNotifications}
                      onChange={(v) => handleSettingChange('pushNotifications', v)}
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notification Types</h3>
                  <div className="space-y-4">
                    <ToggleSetting
                      label="Tender Alerts"
                      description="Get notified about new tenders matching your criteria"
                      icon={FileText}
                      checked={settings.tenderAlerts}
                      onChange={(v) => handleSettingChange('tenderAlerts', v)}
                    />
                    <ToggleSetting
                      label="Proposal Updates"
                      description="Updates on proposal status changes"
                      icon={MessageSquare}
                      checked={settings.proposalUpdates}
                      onChange={(v) => handleSettingChange('proposalUpdates', v)}
                    />
                    <ToggleSetting
                      label="Event Reminders"
                      description="Reminders for upcoming events and deadlines"
                      icon={Calendar}
                      checked={settings.eventReminders}
                      onChange={(v) => handleSettingChange('eventReminders', v)}
                    />
                    <ToggleSetting
                      label="Weekly Digest"
                      description="Receive a weekly summary of activities"
                      icon={Mail}
                      checked={settings.weeklyDigest}
                      onChange={(v) => handleSettingChange('weeklyDigest', v)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Visibility</h3>
                  <div className="space-y-4">
                    <ToggleSetting
                      label="Show Online Status"
                      description="Let others see when you're online"
                      icon={Eye}
                      checked={settings.showOnlineStatus}
                      onChange={(v) => handleSettingChange('showOnlineStatus', v)}
                    />
                    <ToggleSetting
                      label="Show Activity Status"
                      description="Display your recent activity to team members"
                      icon={Eye}
                      checked={settings.showActivityStatus}
                      onChange={(v) => handleSettingChange('showActivityStatus', v)}
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Data & Privacy</h3>
                  <div className="space-y-4">
                    <ToggleSetting
                      label="Usage Analytics"
                      description="Help improve the platform by sharing anonymous usage data"
                      icon={AlertTriangle}
                      checked={settings.allowDataCollection}
                      onChange={(v) => handleSettingChange('allowDataCollection', v)}
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Security</h3>
                  <button 
                    onClick={() => setShowPasswordModal(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-xl hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
                  >
                    <Lock className="w-4 h-4" />
                    Change Password
                  </button>
                </div>
              </div>
            )}

            {/* General Tab */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Regional Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SelectSetting
                      label="Language"
                      value={settings.language}
                      onChange={(v) => handleSettingChange('language', v)}
                      options={[
                        { value: 'en', label: 'English' },
                        { value: 'hi', label: 'Hindi' },
                      ]}
                    />
                    <SelectSetting
                      label="Timezone"
                      value={settings.timezone}
                      onChange={(v) => handleSettingChange('timezone', v)}
                      options={[
                        { value: 'Asia/Kolkata', label: 'India (IST)' },
                        { value: 'America/New_York', label: 'Eastern Time (ET)' },
                        { value: 'Europe/London', label: 'London (GMT)' },
                        { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
                      ]}
                    />
                    <SelectSetting
                      label="Date Format"
                      value={settings.dateFormat}
                      onChange={(v) => handleSettingChange('dateFormat', v)}
                      options={[
                        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                      ]}
                    />
                    <SelectSetting
                      label="Currency"
                      value={settings.currency}
                      onChange={(v) => handleSettingChange('currency', v)}
                      options={[
                        { value: 'INR', label: 'Indian Rupee (₹)' },
                        { value: 'USD', label: 'US Dollar ($)' },
                        { value: 'EUR', label: 'Euro (€)' },
                        { value: 'GBP', label: 'British Pound (£)' },
                      ]}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 shadow-lg shadow-blue-500/25"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white"
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {changingPassword ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Toggle Setting Component
const ToggleSetting = ({ label, description, icon: Icon, checked, onChange }) => (
  <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-100 dark:border-white/5">
    <div className="flex items-center gap-3">
      {Icon && <Icon className="w-5 h-5 text-gray-400" />}
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${
        checked ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
)

// Select Setting Component
const SelectSetting = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
)

export default Settings
